'use no memo';

import type { HolidayPublic } from '@repo/types/holidays';
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';

import { useGetHolidays } from '@repo/api/holiday/useGetHolidays';
import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';

import CreateHolidayDialog from '@/components/features/manage/manage-holidays/CreateHolidayDialog';
import DeleteHolidayDialog from '@/components/features/manage/manage-holidays/DeleteHolidayDialog';
import TanstackTable from '@/components/features/manage/tanstack-table/TanstackTable';
import { useTanstackTable } from '@/components/features/manage/tanstack-table/useTanstackTable';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useTableRowSelection } from '@/hooks/table';

function dateRangeFilterFn(row: { getValue: (id: string) => unknown }, id: string, value: unknown): boolean {
  const v = value as { start?: string | Date; end?: string | Date } | undefined;
  if (!v || (!v.start && !v.end)) return true;
  const dateStr = row.getValue(id) as string | undefined;
  if (!dateStr) return false;
  const date = startOfDay(parseISO(dateStr));
  const filterStart = v.start ? startOfDay(new Date(v.start)) : null;
  const filterEnd = v.end ? endOfDay(new Date(v.end)) : null;
  if (filterStart && filterEnd) return date >= filterStart && date <= filterEnd;
  if (filterStart) return date >= filterStart;
  if (filterEnd) return date <= filterEnd;
  return true;
}

export default function ManageHolidaysPage() {
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [activeHoliday, setActiveHoliday] = useState<HolidayPublic | undefined>(undefined);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data: holidays = [], isPending } = useGetHolidays();

  const columns = useMemo<ColumnDef<HolidayPublic>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected() ? true : table.getIsSomePageRowsSelected() ? 'indeterminate' : false}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label={'Select row'} />,
        enableSorting: false,
        enableHiding: false,
      },
      {
        meta: 'Nazwa',
        id: 'name',
        accessorKey: 'name',
        header: 'Nazwa',
      },
      {
        meta: 'Data początkowa',
        id: 'startDate',
        accessorKey: 'startDate',
        header: 'Data początkowa',
        cell: ({ getValue }) => {
          const val = getValue() as string | undefined;
          return val ? format(parseISO(val), 'dd.MM.yyyy') : '-';
        },
        filterFn: dateRangeFilterFn,
      },
      {
        meta: 'Data końcowa',
        id: 'endDate',
        accessorKey: 'endDate',
        header: 'Data końcowa',
        cell: ({ getValue }) => {
          const val = getValue() as string | undefined;
          return val ? format(parseISO(val), 'dd.MM.yyyy') : '-';
        },
        filterFn: dateRangeFilterFn,
      },
      {
        meta: 'Stworzono',
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Stworzono',
        cell: ({ getValue }) => {
          const val = getValue() as string | undefined;
          return val ? format(new Date(val), 'HH:mm dd.MM.yyyy') : '-';
        },
        filterFn: dateRangeFilterFn,
      },
      {
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const rowOrigin = row.original;
          return (
            <div className={'flex w-full justify-end'}>
              <Button
                variant={'ghost'}
                size={'sm'}
                variantColor={'destructive'}
                onClick={() => {
                  setActiveHoliday(rowOrigin);
                  setOpenDelete(true);
                }}
              >
                <Trash2Icon />
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  const { table, setGlobalFilter } = useTanstackTable({
    name: 'holidays',
    columns,
    data: holidays,
    getRowId: (row) => String(row.id),
    rowSelection: rowSelection,
    onRowSelectionChange: setRowSelection,
    defaultColumnVisibility: {},
  });

  const selectedHolidays = useMemo<Array<HolidayPublic>>(() => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return [];
    const idSet = new Set(selectedIds.map(Number));
    return holidays.filter((holiday) => idSet.has(holiday.id));
  }, [rowSelection, holidays]);

  const { handleRowClick } = useTableRowSelection({
    table,
    setRowSelection,
  });

  const addGroup = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Dodaj dzień wolny',
        icon: <PlusIcon className="size-4" />,
        func: () => setOpenAdd(true),
      },
    ];
  }, []);

  return (
    <>
      <CreateHolidayDialog
        open={openAdd}
        onOpenChange={setOpenAdd}
        defaultValues={{
          startDate: format(new Date(), 'yyyy-MM-dd'),
          endDate: format(new Date(), 'yyyy-MM-dd'),
        }}
      />
      <DeleteHolidayDialog
        holidays={activeHoliday ? [activeHoliday] : selectedHolidays}
        open={openDelete}
        onOpenChange={(open) => {
          setOpenDelete(open);
          if (!open) setActiveHoliday(undefined);
        }}
      />
      <div className={'base-direction-col h-full'}>
        <div className={'viewport-height flex w-full flex-col gap-4'}>
          <TanstackTable
            table={table}
            label={'Dni wolne'}
            isLoading={isPending}
            onGlobalFilterChange={setGlobalFilter}
            filterBy={[
              { id: 'startDate', title: 'Data początkowa', type: 'dateRange' },
              { id: 'endDate', title: 'Data końcowa', type: 'dateRange' },
              { id: 'createdAt', title: 'Data utworzenia', type: 'dateRange' },
            ]}
            onRemoveSelectedRows={() => setOpenDelete(true)}
            addGroup={addGroup}
            onRowClick={handleRowClick}
          />
        </div>
      </div>
    </>
  );
}
